import * as React from 'react';
import './Billing.scss';
import { TK } from '../../store/translations/translationKeys';
import { useTranslations } from '../../store/translations/hooks';
import { ButtonGroup, Button } from 'reactstrap';
import StyledTable from '../../components/StyledTable';
import moment from 'moment';
import { ExpenseData, PaymentData, PaymentOrigin, PaymentStatus } from '../../models';
import { useDispatch, useSelector } from 'react-redux';
import { useEntitiesResult } from '../../store/entities/hooks';
import { ExpenseEntity, PaymentEntity } from '../../store/entities/state';
import { isLoadingEntities } from '../../store/entities/selectors';
import { loadEntities, openEntity, openAppointment } from '../../store/entities/actions';
import { getAdminPaymentsAsync, getClientOrderByIdAsync, getExpensesAsync, getPackAsync } from '../../ajax/requests';
import Typography from '../../components/Typography';
import { formatEuros } from '../../shared/string';
import { DateRangeSelector, DateRangeSelectorState } from '../../components/DateRangeSelector';
import { getRecurringPayments } from './Expenses';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, TooltipContentProps } from "recharts";
import { useBillingGroupBy } from '../../hooks/useBillingGroupBy';
import { CSVLink } from 'react-csv';
import { useCookies } from 'react-cookie';
import { hide } from '../../shared/style';
import { cookiesOptions } from '../../shared/utils';
import { AutocompleteTextField, SwitchField } from '../../components/Forms/fields';
import { TotalsGrid, StatCard, CardLabel, CardValue, CardSubValue } from './BillingStatCards';

interface SerieRecord {date: string, value: number}

const getTotal = (data: SerieRecord[]): number => data.reduce((acc, data) => acc + data.value, 0)

function* getTotalsPerDay(
    outcome: ExpenseData[],
    income: PaymentData[],
    groupBy: 'byDay'|'byWeek'|'byMonth',
    window: DateRangeSelectorState,
): Generator<{date: string, income: number, outcome: number}> {
    const dateFormat = groupBy === 'byMonth' ? 'YYYY-MM' : 'YYYY-MM-DD'

    const outcomePerDay = outcome.reduce<Record<string, SerieRecord[]>>((acc, data) => {
        const day = groupBy === 'byWeek' ? moment.utc(data.payment.date).startOf('isoWeek').format(dateFormat)
            : groupBy === 'byMonth' ? moment.utc(data.payment.date).startOf('month').format(dateFormat)
            : data.payment.date.substring(0, 10)
        return {
            ...acc,
            [day]: acc[day] ? [...acc[day], {...data, ...data.payment}] : [{...data, ...data.payment}],
        };
    }, {})

    const incomePerDay = income.reduce<Record<string, SerieRecord[]>>((acc, data) => {
        const day = groupBy === 'byWeek' ? moment.utc(data.date).startOf('isoWeek').format(dateFormat)
            : groupBy === 'byMonth' ? moment.utc(data.date).startOf('month').format(dateFormat)
            : data.date.substring(0, 10)
        return {
            ...acc,
            [day]: acc[day] ? [...acc[day], data] : [data],
        };
    }, {})

    const from = groupBy === 'byWeek' ? moment.utc(window.from).startOf('isoWeek')
            : groupBy === 'byMonth' ? moment.utc(window.from).startOf('month')
            : moment.utc(window.from)

    const to = moment.utc(window.to).add(1, 'day')
    let current = from

    while (current < to) {
        const day = current.format(dateFormat)

        yield {
            date: day,
            income: getTotal(incomePerDay[day] ?? []),
            outcome: getTotal(outcomePerDay[day] ?? []),
        }

        current = current.add(1, groupBy === 'byWeek' ? 'weeks' : groupBy === 'byMonth' ? 'months' : 'days')
    }
}

const originTk = (origin: PaymentOrigin, plural: boolean = false): TK => {
    switch (origin) {
        case PaymentOrigin.Appointment: return plural ? TK.appointments : TK.appointment
        case PaymentOrigin.Pack: return plural ? TK.packs : TK.pack
        case PaymentOrigin.Order: return TK.orders
        default: return TK.unknown
    }
}

const removeVAT = (value: number, vat?: number) =>
    vat ? value / (1 + vat) : value;

const InVsOut: React.FC = () => {
    const t = useTranslations()
    const dispatch = useDispatch()
    const outcomeRaw = useEntitiesResult<ExpenseEntity>('expenses')
    const income = useEntitiesResult<PaymentEntity>('payments')
    const isLoadingExpenses = useSelector(isLoadingEntities('expenses'))
    const isLoadingPayments = useSelector(isLoadingEntities('payments'))
    const isLoading = isLoadingExpenses || isLoadingPayments

    const [dates, setDates] = React.useState<DateRangeSelectorState>({
        from: moment().startOf('month').format('YYYY-MM-DD'),
        to: moment().endOf('month').format('YYYY-MM-DD'),
        type: 'month',
    })

    const [cookies, setCookie] = useCookies()
    const [groupBy, setGroupBy] = useBillingGroupBy()
    const [withoutVAT, setWithoutVAT] = React.useState(false)
    const [dontGroup, setDontGroup] = React.useState<boolean>(!!cookies['BILLING_DONT_GROUP'])

    const [incomeFilters, setIncomeFilters] = React.useState<string[]>([])
    const [invertIncomeFilter, setInvertIncomeFilter] = React.useState(false)
    const [outcomeFilters, setOutcomeFilters] = React.useState<string[]>([])
    const [invertOutcomeFilter, setInvertOutcomeFilter] = React.useState(false)

    const filteredIncome = React.useMemo(() => {
        const valid = income.filter(p => p.status === PaymentStatus.Manual || p.status === PaymentStatus.Success)
        if (!incomeFilters.length) return valid
        return valid.filter(p => {
            const matches = incomeFilters.includes(p.clientName) ||
                incomeFilters.includes(t(originTk(p.origin))) ||
                incomeFilters.includes(p.type) ||
                incomeFilters.includes(p.cashRegisterName ?? '') ||
                incomeFilters.includes(p.username ?? '')
            return invertIncomeFilter ? !matches : matches
        })
    }, [income, incomeFilters, invertIncomeFilter])

    const filteredOutcomeRaw = React.useMemo(() => {
        if (!outcomeFilters.length) return outcomeRaw
        return outcomeRaw.filter(e => {
            const matches = outcomeFilters.includes(e.name) ||
                outcomeFilters.includes(e.type) ||
                outcomeFilters.includes(e.payment.type)
            return invertOutcomeFilter ? !matches : matches
        })
    }, [outcomeRaw, outcomeFilters, invertOutcomeFilter])

    const outcome = React.useMemo(() => filteredOutcomeRaw.flatMap<ExpenseData>(e => e.recurring
            ? getRecurringPayments(e, moment.utc(dates.from), moment.utc(dates.to))
            : [e])
        , [filteredOutcomeRaw, dates])

    const hasVAT = React.useMemo(() =>
        filteredIncome.some(p => p.vat) || outcome.some(e => e.payment.vat)
    , [filteredIncome, outcome])

    const processedIncome = React.useMemo(() =>
        withoutVAT
            ? filteredIncome.map(p => p.vat ? { ...p, value: removeVAT(p.value, p.vat) } : p)
            : filteredIncome
    , [filteredIncome, withoutVAT])

    const processedOutcome = React.useMemo(() =>
        withoutVAT
            ? outcome.map(e => e.payment.vat ? { ...e, payment: { ...e.payment, value: removeVAT(e.payment.value, e.payment.vat) } } : e)
            : outcome
    , [outcome, withoutVAT])

    React.useEffect(() => {
        if (dates) {
            const diff = moment.utc(dates.to).diff(moment.utc(dates.from), 'months')
            setGroupBy(diff <= 1 ? 'byDay' : 'byMonth')
            dispatch(loadEntities('expenses', () => getExpensesAsync(dates.from, dates.to), 'NewResult', outcomeRaw.map(x => x.id)))
            dispatch(loadEntities('payments', () => getAdminPaymentsAsync(dates.from, dates.to), 'NewResult', income.map(x => x.id)))
        }
    }, [dates])

    const data = [...getTotalsPerDay(processedOutcome, processedIncome, groupBy, dates)]

    const totalIncome = getTotal(data.map(d => ({ date: d.date, value: d.income })))
    const totalOutcome = getTotal(data.map(d => ({ date: d.date, value: d.outcome })))
    const totalNet = totalIncome - totalOutcome

    const totalIncomeVAT = React.useMemo(() =>
        filteredIncome.reduce((acc, p) => acc + (p.vat ? p.value - removeVAT(p.value, p.vat) : 0), 0)
    , [filteredIncome])

    const totalOutcomeVAT = React.useMemo(() =>
        outcome.reduce((acc, e) => acc + (e.payment.vat ? e.payment.value - removeVAT(e.payment.value, e.payment.vat) : 0), 0)
    , [outcome])

    const csvData = React.useMemo(() => data.map(d => ({
        date: d.date,
        income: formatEuros(d.income),
        outcome: formatEuros(d.outcome),
        net: formatEuros(d.income - d.outcome),
    })), [data]);

    const handlePaymentClick = React.useCallback((payment: PaymentEntity) => {
        if (payment.origin === PaymentOrigin.Appointment) {
            dispatch(openAppointment(payment.originId))
        } else if (payment.origin === PaymentOrigin.Pack) {
            dispatch(openEntity('clientPacks', payment.originId, () => getPackAsync(payment.originId)))
        } else if (payment.origin === PaymentOrigin.Order) {
            dispatch(openEntity('orders', payment.originId, () => getClientOrderByIdAsync(payment.originId) as any))
        }
    }, [dispatch])

    const handleExpenseClick = React.useCallback((expenseId: string) => {
        const expense = outcomeRaw.find(e => e.id === expenseId)
        if (!expense) return;
        dispatch(openEntity('expenses', expense.id, () => Promise.resolve(expense)))
    }, [outcomeRaw, dispatch])

    const combinedData = React.useMemo(() => {
        const incomeItems = processedIncome.map(p => ({
            date: p.date.substring(0, 16).replace('T', ' '),
            sortDate: p.date,
            description: p.clientName,
            payment: p as PaymentEntity,
            expenseId: undefined as string | undefined,
            kind: 'income' as const,
            value: p.value,
        }))
        const outcomeItems = processedOutcome.map(e => ({
            date: e.payment.date.substring(0, 10),
            sortDate: e.payment.date,
            description: `${e.name} (${e.type})`,
            payment: undefined as PaymentEntity | undefined,
            expenseId: e.id as string | undefined,
            kind: 'outcome' as const,
            value: e.payment.value,
        }))
        return [...incomeItems, ...outcomeItems].sort((a, b) => a.sortDate.localeCompare(b.sortDate))
    }, [processedIncome, processedOutcome])

    const CustomTooltip = ({ active, payload, label }: TooltipContentProps<string | number, string>) => {
        const isVisible = active && payload && payload.length;
        return (
            <div style={{ visibility: isVisible ? 'visible' : 'hidden', display: 'flex', flexDirection: 'column' }}>
            {isVisible && (
                <>
                    <span><b>{label}</b></span>
                    {payload.map(d => (
                        <span style={{color: d.color}}>{t(d.dataKey)}: {formatEuros(d.value)}</span>
                    ))}
                </>
            )}
            </div>
        );
    };

    return (
        <div className='billingContainer'>
            {isLoading && <Typography style={{position: 'fixed', bottom: 0, left: 0, backgroundColor: 'white'}}>{t(TK.loading)}...</Typography>}

            <DateRangeSelector onRangeChanged={setDates}/>


            <div className="billTypePanel">
                <ButtonGroup >
                    <Button outline color="info" active={groupBy === 'byDay'} onClick={() => setGroupBy('byDay')}>{t(TK.byDay)}</Button>
                    <Button outline color="info" active={groupBy === 'byWeek'} onClick={() => setGroupBy('byWeek')}>{t(TK.byWeek)}</Button>
                    <Button outline color="info" active={groupBy === 'byMonth'} onClick={() => setGroupBy('byMonth')}>{t(TK.byMonth)}</Button>
                </ButtonGroup>
                <AutocompleteTextField
                    options={[...new Set(income.flatMap(p => [p.clientName, t(originTk(p.origin)), p.type, p.cashRegisterName, p.username].filter((v): v is string => !!v)))]}
                    values={incomeFilters}
                    onValuesChanged={setIncomeFilters}
                    multiSelect
                    placeholder={`${t(TK.filter)} ${t(TK.income).toLowerCase()}...`}
                />
                <AutocompleteTextField
                    options={[...new Set(outcomeRaw.flatMap(e => [e.name, e.type, e.payment.type].filter(Boolean)))]}
                    values={outcomeFilters}
                    onValuesChanged={setOutcomeFilters}
                    multiSelect
                    placeholder={`${t(TK.filter)} ${t(TK.outcome).toLowerCase()}...`}
                />
            </div>

            { isLoading ? <Typography>{t(TK.loading)}...</Typography> 
                : <>
                    <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 'var(--spacer-M)', padding: 'var(--spacer-XS) 0', borderBottom: 'var(--border-thin) solid var(--appLigthGrey)', marginBottom: 'var(--spacer-S)'}}>
                        {incomeFilters.length > 0 && (
                            <SwitchField label={`${t(TK.invertFilterResult)} (${t(TK.income).toLowerCase()})`} value={invertIncomeFilter} onChange={setInvertIncomeFilter} />
                        )}
                        {outcomeFilters.length > 0 && (
                            <SwitchField label={`${t(TK.invertFilterResult)} (${t(TK.outcome).toLowerCase()})`} value={invertOutcomeFilter} onChange={setInvertOutcomeFilter} />
                        )}
                        {hasVAT && <SwitchField label={t(TK.withoutVAT)} value={withoutVAT} onChange={setWithoutVAT} /> }
                    </div>
                    
                    <TotalsGrid>
                        <StatCard accent="var(--success-color)">
                            <CardLabel>{t(TK.income)}</CardLabel>
                            <CardValue textColor="var(--success-color)">{formatEuros(totalIncome)}</CardValue>
                            {hasVAT && !withoutVAT && (
                                <CardSubValue>{t(TK.vat)}: {formatEuros(totalIncomeVAT)}</CardSubValue>
                            )}
                        </StatCard>
                        <StatCard accent="var(--danger-color)">
                            <CardLabel>{t(TK.outcome)}</CardLabel>
                            <CardValue textColor="var(--danger-color)">{formatEuros(totalOutcome)}</CardValue>
                            {hasVAT && !withoutVAT && (
                                <CardSubValue>{t(TK.vat)}: {formatEuros(totalOutcomeVAT)}</CardSubValue>
                            )}
                        </StatCard>
                        <StatCard accent={totalNet >= 0 ? 'var(--success-color)' : 'var(--danger-color)'}>
                            <CardLabel>Net</CardLabel>
                            <CardValue textColor={totalNet >= 0 ? 'var(--success-color)' : 'var(--danger-color)'}>{formatEuros(totalNet)}</CardValue>
                            {hasVAT && !withoutVAT && (
                                <CardSubValue>{t(TK.vat)}: {formatEuros(totalIncomeVAT - totalOutcomeVAT)}</CardSubValue>
                            )}
                        </StatCard>
                    </TotalsGrid>

                    <LineChart
                        style={{ width: '100%', height: '100%', maxHeight: '500px' }}
                        responsive
                        data={data}
                        margin={{
                            top: 5,
                            right: 0,
                            left: 0,
                            bottom: 5,
                        }}
                        >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis width="auto" unit="€" />
                        <Tooltip content={CustomTooltip} />
                        <Legend  />
                        <Line type="monotone" dataKey="income" name={t(TK.income)} stroke="#6ca833ff" activeDot={{ r: 8 }} />
                        <Line type="monotone" dataKey="outcome" name={t(TK.outcome)} stroke="#c45955ff" />
                    </LineChart>

                    <div style={{display: 'flex', gap: 'var(--spacer-XS)', justifyContent: 'end', marginTop: 'var(--spacer-S)'}}>
                        <SwitchField
                            style={{display: 'inline'}}
                            label={t(TK.dontGroup)}
                            value={dontGroup}
                            id='dont-group'
                            onChange={e => {
                                const val = !!e
                                setDontGroup(val)
                                setCookie('BILLING_DONT_GROUP', val ? '1' : '', cookiesOptions)
                            }}
                        />
                    </div>

                    <div className="tableContainer" style={{marginTop: 'var(--spacer-S)', ...hide(dontGroup)}}>
                        <StyledTable>
                            <thead>
                                <tr>
                                    <th>{t(TK.date)}</th>
                                    <th>{t(TK.income)}</th>
                                    <th>{t(TK.outcome)}</th>
                                    <th>Net</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((item, i) => (
                                    <tr key={`${item.date}-${i}`}>
                                        <td>{item.date}</td>
                                        <td style={{color: 'var(--success-color)', fontWeight: 600}}>{formatEuros(item.income)}</td>
                                        <td style={{color: 'var(--danger-color)', fontWeight: 600}}>{formatEuros(item.outcome)}</td>
                                        <td style={{
                                            fontWeight: 600,
                                            color: item.income === item.outcome ? 'var(--text-muted)' : item.income - item.outcome >= 0 ? 'var(--success-color)' : 'var(--danger-color)'
                                        }}>
                                            {formatEuros(item.income - item.outcome)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </StyledTable>
                    </div>

                    <div className="tableContainer" style={{marginTop: 'var(--spacer-S)', ...hide(!dontGroup)}}>
                        <StyledTable scrollable>
                            <thead>
                                <tr>
                                    <th>{t(TK.date)}</th>
                                    <th>{t(TK.name)}</th>
                                    <th>{t(TK.income)}</th>
                                    <th>{t(TK.outcome)}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {combinedData.map((item, i) => (
                                    <tr key={`${item.sortDate}-${item.kind}-${i}`}>
                                        <td>{item.date}</td>
                                        <td>
                                            {item.expenseId ? (
                                                <Button size="sm" color="link" style={{padding: 0}} onClick={() => handleExpenseClick(item.expenseId!)}>{item.description}</Button>
                                            ) : (
                                                item.description
                                            )}
                                            {item.payment && (
                                                <> (<Button size="sm" color="link" style={{padding: 0, verticalAlign: 'baseline'}} onClick={() => handlePaymentClick(item.payment!)}>{t(originTk(item.payment!.origin))}</Button>)</>
                                            )}
                                        </td>
                                        <td style={{color: 'var(--success-color)', fontWeight: 600}}>
                                            {item.kind === 'income' ? formatEuros(item.value) : ''}
                                        </td>
                                        <td style={{color: 'var(--danger-color)', fontWeight: 600}}>
                                            {item.kind === 'outcome' ? formatEuros(item.value) : ''}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </StyledTable>
                    </div>

                    <CSVLink
                        data={csvData}
                        separator=';'
                        target='_blank'
                        filename='revenue-vs-expenses-export.csv'
                    >
                        {t(TK.exportData)}
                    </CSVLink>
                </>
            }
        </div>
    );
}

export default InVsOut;
